<template>
  <div class="mod-panel">
    <button class="mod-back" type="button" @click="$emit('back')">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
      Voltar
    </button>

    <div v-if="loading" class="mod-loading">Carregando...</div>

    <template v-else-if="os">
      <div class="mod-header">
        <div>
          <div class="mod-title">OS{{ os.osNumber }} — {{ os.title }}</div>
          <div class="mod-meta">
            {{ os.responsaveis.join(', ') }}
            <span v-if="os.dueDate"> · prazo {{ formatDate(os.dueDate) }}</span>
            <span v-if="os.requireAll"> · todos precisam preencher</span>
            <span class="mod-priority-badge" :class="'p-' + os.priority">{{ priorityLabel(os.priority) }}</span>
            <span class="mod-status-badge" :class="'s-' + os.status">{{ statusLabel(os.status) }}</span>
          </div>
        </div>
        <div v-if="accessLevel >= 2" class="mod-actions">
          <template v-if="os.status !== 'cancelled'">
            <button class="mod-btn" type="button" @click="showAddItem = !showAddItem">+ Adicionar rotina</button>
            <button class="mod-btn mod-btn-danger" type="button" @click="cancelOs">Cancelar OS</button>
          </template>
          <button v-if="accessLevel >= 3" class="mod-btn mod-btn-danger" type="button" @click="deleteOs">Apagar OS</button>
        </div>
      </div>

      <div v-if="showAddItem" class="mod-new-form">
        <div class="mod-grid">
          <div class="mod-field">
            <label class="mod-label">Título da rotina</label>
            <input class="mod-input" type="text" v-model="newItem.title" placeholder="Ex.: Verificar extintores" />
          </div>
          <div class="mod-field">
            <label class="mod-label">Descrição (opcional)</label>
            <input class="mod-input" type="text" v-model="newItem.description" />
          </div>
        </div>
        <p v-if="addItemError" class="mod-error">{{ addItemError }}</p>
        <div class="mod-form-actions">
          <button class="mod-btn mod-btn-primary" type="button" :disabled="addingItem" @click="submitAddItem">
            {{ addingItem ? 'Adicionando...' : 'Adicionar rotina' }}
          </button>
        </div>
      </div>

      <div class="mod-items">
        <div v-for="item in os.items" :key="item.id" class="mod-item-card">
          <template v-if="editingItem === item.id">
            <div class="mod-grid">
              <div class="mod-field">
                <label class="mod-label">Título</label>
                <input class="mod-input" type="text" v-model="editItemForm.title" />
              </div>
              <div class="mod-field">
                <label class="mod-label">Descrição</label>
                <input class="mod-input" type="text" v-model="editItemForm.description" />
              </div>
            </div>
            <div class="mod-form-actions">
              <button class="mod-btn" type="button" @click="editingItem = ''">Cancelar</button>
              <button class="mod-btn mod-btn-primary" type="button" @click="submitEditItem(item.id)">Salvar</button>
            </div>
          </template>
          <template v-else>
            <div class="mod-item-top">
              <div>
                <div class="mod-item-title">{{ item.title }}</div>
                <div v-if="item.description" class="mod-item-desc">{{ item.description }}</div>
              </div>
              <span class="mod-status-badge" :class="'s-' + item.status">{{ itemStatusLabel(item.status) }}</span>
            </div>

            <div v-if="item.status !== 'pending' || item.completedAt" class="mod-item-evidence">
              <div v-if="item.textResponse" class="mod-evidence-line">{{ item.textResponse }}</div>
              <div v-if="item.checkResponse" class="mod-evidence-line">✓ Marcado como feito</div>
              <img v-if="item.photoResponse" :src="item.photoResponse" class="mod-evidence-photo" alt="Evidência" />
              <div v-if="item.completedByName" class="mod-evidence-meta">Concluído por {{ item.completedByName }} em {{ formatDateTime(item.completedAt) }}</div>
            </div>

            <div v-if="item.reviewNotes && item.status === 'pending'" class="mod-review-note">
              Reprovado por {{ item.reviewedByName }}: {{ item.reviewNotes }}
            </div>

            <div class="mod-item-actions">
              <template v-if="accessLevel >= 3 && item.status === 'done'">
                <button class="mod-btn mod-btn-primary" type="button" @click="approve(item.id)">Aprovar</button>
                <button class="mod-btn mod-btn-danger" type="button" @click="openReject(item.id)">Reprovar</button>
              </template>
              <template v-if="accessLevel >= 2 && os.status !== 'cancelled'">
                <button class="mod-btn" type="button" @click="openEditItem(item)">Editar</button>
                <button class="mod-btn mod-btn-danger" type="button" @click="removeItem(item.id)">Remover</button>
              </template>
            </div>

            <div v-if="rejecting === item.id" class="mod-reject-form">
              <textarea class="mod-textarea" v-model="rejectNotes" rows="2" placeholder="Motivo da reprovação (obrigatório)"></textarea>
              <div class="mod-form-actions">
                <button class="mod-btn" type="button" @click="rejecting = ''">Cancelar</button>
                <button class="mod-btn mod-btn-danger" type="button" :disabled="!rejectNotes.trim()" @click="reject(item.id)">Confirmar reprovação</button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from 'vue'
import { maintenanceStore, type ServiceOrderDetail } from '@/store/maintenance'

export default defineComponent({
  name: 'MaintenanceOSInstanceDetail',
  props: {
    osId: { type: String, required: true },
    accessLevel: { type: Number, default: 0 }
  },
  emits: ['back', 'changed'],
  setup(props, { emit }) {
    const loading = ref(true)
    const os = ref<ServiceOrderDetail | null>(null)
    const showAddItem = ref(false)
    const addingItem = ref(false)
    const addItemError = ref('')
    const newItem = ref({ title: '', description: '' })
    const editingItem = ref('')
    const editItemForm = ref({ title: '', description: '' })
    const rejecting = ref('')
    const rejectNotes = ref('')

    const priorityLabel = (p: string) => ({ baixa: 'Baixa', normal: 'Normal', alta: 'Alta', urgente: 'Urgente' }[p] || 'Normal')
    const statusLabel = (s: string) => ({ pending: 'Em andamento', approved: 'Aprovada', cancelled: 'Cancelada' }[s] || s)
    const itemStatusLabel = (s: string) => ({ pending: 'Pendente', done: 'Aguardando revisão', approved: 'Aprovado' }[s] || s)
    const formatDate = (iso: string) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }
    const formatDateTime = (iso: string) => iso ? new Date(iso).toLocaleString('pt-BR') : ''

    const load = async () => {
      loading.value = true
      try {
        os.value = await maintenanceStore.loadServiceOrderDetail(props.osId)
      } finally {
        loading.value = false
      }
    }

    const submitAddItem = async () => {
      addItemError.value = ''
      if (!newItem.value.title.trim()) {
        addItemError.value = 'Informe o título da rotina.'
        return
      }
      addingItem.value = true
      try {
        await maintenanceStore.addServiceOrderItem(props.osId, { title: newItem.value.title.trim(), description: newItem.value.description.trim() })
        newItem.value = { title: '', description: '' }
        showAddItem.value = false
        await load()
        emit('changed')
      } catch (err) {
        addItemError.value = err instanceof Error ? err.message : 'Erro ao adicionar rotina.'
      } finally {
        addingItem.value = false
      }
    }

    const openEditItem = (item: { id: string; title: string; description: string }) => {
      editingItem.value = item.id
      editItemForm.value = { title: item.title, description: item.description }
    }
    const submitEditItem = async (itemId: string) => {
      if (!editItemForm.value.title.trim()) return
      await maintenanceStore.updateServiceOrderItem(itemId, { title: editItemForm.value.title.trim(), description: editItemForm.value.description.trim() })
      editingItem.value = ''
      await load()
      emit('changed')
    }
    const removeItem = async (itemId: string) => {
      await maintenanceStore.deleteServiceOrderItem(itemId)
      await load()
      emit('changed')
    }

    const cancelOs = async () => {
      await maintenanceStore.cancelServiceOrder(props.osId)
      emit('changed')
      emit('back')
    }
    const deleteOs = async () => {
      await maintenanceStore.deleteServiceOrder(props.osId)
      emit('changed')
      emit('back')
    }

    const approve = async (itemId: string) => {
      await maintenanceStore.reviewServiceOrderItem(itemId, { approve: true })
      await load()
      emit('changed')
    }
    const openReject = (itemId: string) => { rejecting.value = itemId; rejectNotes.value = '' }
    const reject = async (itemId: string) => {
      await maintenanceStore.reviewServiceOrderItem(itemId, { approve: false, notes: rejectNotes.value.trim() })
      rejecting.value = ''
      await load()
      emit('changed')
    }

    watch(() => props.osId, load)
    onMounted(load)

    return {
      loading, os, showAddItem, addingItem, addItemError, newItem,
      editingItem, editItemForm, rejecting, rejectNotes,
      priorityLabel, statusLabel, itemStatusLabel, formatDate, formatDateTime,
      submitAddItem, openEditItem, submitEditItem, removeItem, cancelOs, deleteOs, approve, openReject, reject
    }
  }
})
</script>

<style scoped lang="scss">
.mod-panel { display: flex; flex-direction: column; gap: 16px; }
.mod-back { display: flex; align-items: center; gap: 4px; background: none; border: none; color: var(--ink-muted); font-size: 13px; cursor: pointer; padding: 4px 6px; align-self: flex-start; }
.mod-loading { font-size: 13px; color: var(--ink-muted); padding: 24px 0; text-align: center; }

.mod-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.mod-title { font-size: 16px; font-weight: 700; color: var(--ink); }
.mod-meta { font-size: 12px; color: var(--ink-muted); display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.mod-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.mod-new-form {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
}
.mod-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
.mod-field { display: flex; flex-direction: column; gap: 4px; }
.mod-label { font-size: 12px; font-weight: 600; color: var(--ink-muted); }
.mod-input, .mod-textarea {
  border: 1px solid rgba(31,30,26,.18); border-radius: 8px; padding: 8px 10px;
  font-size: 13px; color: var(--ink); background: var(--bg, #fff); font-family: inherit;
}
.mod-textarea { resize: vertical; }
.mod-error { color: #dc2626; font-size: 12px; margin: 0; }
.mod-form-actions { display: flex; justify-content: flex-end; gap: 8px; }

.mod-btn {
  border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
  background: rgba(31,30,26,.08); color: var(--ink);
  &:disabled { opacity: .6; cursor: default; }
}
.mod-btn-primary { background: var(--accent); color: #fff; }
.mod-btn-danger { background: rgba(220,38,38,.1); color: #dc2626; }

.mod-items { display: flex; flex-direction: column; gap: 10px; }
.mod-item-card {
  background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 12px;
  padding: 14px 16px; display: flex; flex-direction: column; gap: 10px;
}
.mod-item-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.mod-item-title { font-size: 13px; font-weight: 700; color: var(--ink); }
.mod-item-desc { font-size: 12px; color: var(--ink-muted); }
.mod-item-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.mod-item-evidence { background: rgba(31,30,26,.03); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 6px; font-size: 12px; }
.mod-evidence-line { color: var(--ink); }
.mod-evidence-meta { color: var(--ink-muted); }
.mod-evidence-photo { max-width: 200px; border-radius: 8px; }

.mod-review-note { background: rgba(220,38,38,.08); color: #b91c1c; border-radius: 8px; padding: 8px 10px; font-size: 12px; }
.mod-reject-form { display: flex; flex-direction: column; gap: 8px; }

.mod-priority-badge, .mod-status-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-left: 6px;
}
.mod-priority-badge {
  &.p-baixa { background: rgba(100,116,139,.12); color: #475569; }
  &.p-normal { background: rgba(37,99,235,.12); color: #2563eb; }
  &.p-alta { background: rgba(217,119,6,.12); color: #d97706; }
  &.p-urgente { background: rgba(220,38,38,.12); color: #dc2626; }
}
.mod-status-badge {
  &.s-pending { background: rgba(217,119,6,.12); color: #d97706; }
  &.s-done { background: rgba(37,99,235,.12); color: #2563eb; }
  &.s-approved { background: rgba(22,163,74,.12); color: #16a34a; }
  &.s-cancelled { background: rgba(100,116,139,.12); color: #475569; }
}
</style>
